import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import SegurancaPage from "./pages/SegurancaPage";
import ChatPage from "./pages/ChatPage";
import SubscribePage from "./pages/SubscribePage";
import AccountPage from "./pages/AccountPage";
import AccountManagementPage from "./pages/AccountManagementPage";
import TermsPage from "./pages/TermsPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import { useFeatureFlags } from "./utils/featureFlags/useFeatureFlags";
import { SubscriptionInfo } from "./services/api";

// Acesso ao Weedy: assinatura ativa OU cancelada ainda dentro da janela já paga
// (mantém acesso até accessUntil — fim do ciclo/compromisso/trial).
export function hasWeedyAccess(sub: SubscriptionInfo | null): boolean {
  if (!sub) return false;
  if (sub.status === "active") return true;
  if (sub.status === "canceled" && sub.accessUntil) {
    return new Date(sub.accessUntil) > new Date();
  }
  return false;
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #1a313c 0%, #04090a 100%)",
          color: "#ffffff",
          fontFamily: "Inter, system-ui, sans-serif",
          padding: "24px",
          textAlign: "center",
        }}>
          <h1 style={{ fontSize: "24px", fontWeight: 700, marginBottom: "12px", fontFamily: "Oxanium, sans-serif" }}>
            Algo deu errado
          </h1>
          <p style={{ fontSize: "16px", color: "rgba(255,255,255,0.7)", marginBottom: "24px", maxWidth: "400px" }}>
            Ocorreu um erro inesperado. Tente recarregar a pagina.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: "linear-gradient(135deg, #E2725B 0%, #CC3B2A 100%)",
              color: "#ffffff",
              border: "none",
              borderRadius: "16px",
              padding: "14px 32px",
              fontSize: "16px",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Recarregar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Os guards só decidem depois que o AuthContext confere a sessão (GET
// /access/me, termos e assinatura). Antes disso tudo parece "deslogado": quem
// recarregava /account ia para /login e, quando a sessão chegava, para /chat.
// O /login e o /forgot não esperam: o LoginPage usa o mesmo `loading` no botão.
function SessionLoader() {
  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(135deg, #1a313c 0%, #04090a 100%)",
    }}>
      <CircularProgress aria-label="Carregando" sx={{ color: "#E2725B" }} />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { loading, isAuthenticated } = useAuth();
  if (loading) return <SessionLoader />;
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function FirstLoginRoute({ children }: { children: React.ReactNode }) {
  const { loading, isAuthenticated, isFirstLogin } = useAuth();
  if (loading) return <SessionLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (isFirstLogin) return <Navigate to="/change-password" replace />;
  return <>{children}</>;
}

function TermsRoute({ children }: { children: React.ReactNode }) {
  const { loading, isAuthenticated, isFirstLogin, needsTermsAcceptance } = useAuth();
  if (loading) return <SessionLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (isFirstLogin) return <Navigate to="/change-password" replace />;
  if (needsTermsAcceptance) return <Navigate to="/terms" replace />;
  return <>{children}</>;
}

function SubscriptionRoute({ children }: { children: React.ReactNode }) {
  const { loading, isAuthenticated, isFirstLogin, needsTermsAcceptance, subscription } = useAuth();
  if (loading) return <SessionLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (isFirstLogin) return <Navigate to="/change-password" replace />;
  if (needsTermsAcceptance) return <Navigate to="/terms" replace />;
  // Cobrança falhou → manda para a gestão para atualizar o cartão.
  if (subscription?.status === "failed") return <Navigate to="/account" replace />;
  if (!hasWeedyAccess(subscription)) return <Navigate to="/subscribe" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  const { isAuthenticated, isFirstLogin, needsTermsAcceptance, subscription } = useAuth();
  const location = useLocation();
  const { pathname } = location;
  const { flags } = useFeatureFlags();

  // PageView do Meta Pixel. Fica aqui e não no snippet do index.html porque
  // isto é SPA: o navegador só carrega a página uma vez, então o snippet
  // contaria só a rota de entrada e o resto do funil sumiria.
  React.useEffect(() => {
    window.fbq?.("track", "PageView");
  }, [pathname]);

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/signup" element={<SignupPage />} />
      {/* Sem gate: é o documento que o quadro de segurança do chat referencia,
          conteúdo genérico e sem dado de paciente. Abre em aba nova (ver o
          override de `a` em ChatMessage), então não pode depender de sessão. */}
      <Route path="/seguranca" element={<SegurancaPage />} />
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            isFirstLogin ? <Navigate to="/change-password" replace /> : <Navigate to="/chat" replace />
          ) : (
            <LoginPage />
          )
        }
      />
      <Route
        path="/forgot"
        element={
          isAuthenticated ? <Navigate to="/chat" replace /> : <ForgotPasswordPage />
        }
      />
      <Route
        path="/change-password"
        element={
          <ProtectedRoute>
            {isFirstLogin || location.state?.from === '/account' ? (
              <ChangePasswordPage />
            ) : (
              <Navigate to="/chat" replace />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/terms"
        element={
          <FirstLoginRoute>
            {needsTermsAcceptance ? (
              <TermsPage />
            ) : (
              <Navigate to="/chat" replace />
            )}
          </FirstLoginRoute>
        }
      />
      <Route
        path="/subscribe"
        element={
          <TermsRoute>
            {hasWeedyAccess(subscription) ? (
              <Navigate to="/chat" replace />
            ) : (
              <SubscribePage />
            )}
          </TermsRoute>
        }
      />
      <Route
        path="/account"
        element={
          <TermsRoute>
            {/* Quando a flag estiver ativa, exibe a nova tela de gestão.
                Quando desligada, mantém AccountPage para preservar o
                redirect de billing failed (status === "failed") sem loop. */}
            {flags.accountManagement ? (
              <AccountManagementPage />
            ) : (
              <AccountPage />
            )}
          </TermsRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <SubscriptionRoute>
            <ChatPage />
          </SubscriptionRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
